/**
 * CookieConsent.js  v3.0
 * Enterprise cookie consent system for Kaphy Insurance Agency.
 *
 * New in v3.0:
 *  - Loads live config from /api/cookie-consent/config (graceful offline fallback)
 *  - Consent expiry based on admin-configured renewalInterval
 *  - Policy-version change detection → forces re-consent when policy bumped
 *  - Non-blocking event tracking sent to /api/cookie-consent/event
 *  - Listens for global 'kaphyCookieSettingsOpen' event so the footer link
 *    can re-open the preferences modal at any time
 *  - Banner and modal can render independently (modal opens even after banner dismissed)
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./CookieConsentStyle.css";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://kaphy-insurance-api-24bfecb5d4bb.herokuapp.com";

const STORAGE_KEY     = "kaphyCookieConsent";
const CONSENT_VERSION = "2.0";

// Maps renewalInterval strings to milliseconds
const RENEWAL_MS = {
  weekly:   7   * 86_400_000,
  monthly:  30  * 86_400_000,
  "3months": 90  * 86_400_000,
  "6months": 180 * 86_400_000,
  yearly:   365 * 86_400_000,
  never:    Infinity,
};

const DEFAULT_PREFS = {
  essential:  true,
  analytics:  false,
  marketing:  false,
  functional: false,
};

// ── Shared helpers ────────────────────────────────────────────────────────────

function migrateStoredConsent(raw) {
  if (!raw) return null;
  if (raw === "all")       return { essential: true, analytics: true,  marketing: true,  functional: true  };
  if (raw === "necessary") return { essential: true, analytics: false, marketing: false, functional: false };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "essential" in parsed) return parsed;
  } catch {}
  return null;
}

function persistConsent(prefs, policyVersion) {
  const payload = {
    ...prefs,
    essential:     true,
    timestamp:     Date.now(),
    version:       CONSENT_VERSION,
    policyVersion: policyVersion || "1.0",
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

// Stable single session ID — never tied to a user account
function getSessionId() {
  let sid = sessionStorage.getItem("kaphySid");
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("kaphySid", sid);
  }
  return sid;
}

// ── Static icon/category definitions (text overridden by admin config) ────────

const CATEGORIES_STATIC = [
  {
    id: "essential",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" fill="url(#ess-grad)" />
        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <defs>
          <linearGradient id="ess-grad" x1="3" y1="2" x2="21" y2="23" gradientUnits="userSpaceOnUse">
            <stop stopColor="#3b82f6" /><stop offset="1" stopColor="#1d4ed8" />
          </linearGradient>
        </defs>
      </svg>
    ),
    name:        "Essential",
    description: "Required for core website functionality. Cannot be disabled.",
    details:     "These cookies enable basic functions like page navigation, secure login, and access to protected areas. The website cannot function properly without them.",
    examples:    ["Session management", "Security tokens", "Load balancing", "Authentication"],
    alwaysOn:    true,
  },
  {
    id: "analytics",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    name:        "Analytics",
    description: "Help us understand how visitors interact with our website.",
    details:     "Analytics cookies let us measure traffic, identify popular content, and improve your experience. All data is anonymized and aggregated — we never sell it.",
    examples:    ["Page view tracking", "Traffic sources", "User journeys", "Performance metrics"],
    alwaysOn:    false,
  },
  {
    id: "marketing",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
        <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    name:        "Marketing",
    description: "Used to deliver relevant insurance offers and personalized content.",
    details:     "Marketing cookies track browsing to show you relevant insurance products. Data may be shared with trusted advertising partners. You can opt out at any time.",
    examples:    ["Targeted insurance ads", "Remarketing campaigns", "Social media integration", "Conversion tracking"],
    alwaysOn:    false,
  },
  {
    id: "functional",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    name:        "Functional",
    description: "Enable enhanced features and personalized settings.",
    details:     "Functional cookies remember your preferences and settings to provide a more personalized experience, such as saved language and region choices.",
    examples:    ["Language preferences", "Region settings", "Chat support", "Saved form data"],
    alwaysOn:    false,
  },
];

const ShieldIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" fill="url(#banner-shield)" />
    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <defs>
      <linearGradient id="banner-shield" x1="3" y1="2" x2="21" y2="23" gradientUnits="userSpaceOnUse">
        <stop stopColor="#3b82f6" /><stop offset="1" stopColor="#1d4ed8" />
      </linearGradient>
    </defs>
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

const CookieConsent = () => {
  const [showBanner,     setShowBanner]     = useState(false);
  const [bannerVisible,  setBannerVisible]  = useState(false);
  const [modalOpen,      setModalOpen]      = useState(false);
  const [modalVisible,   setModalVisible]   = useState(false);
  const [preferences,    setPreferences]    = useState({ ...DEFAULT_PREFS });
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [saved,          setSaved]          = useState(false);

  // Live config loaded from the backend (null = not yet loaded)
  const [config,       setConfig]       = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const configRef         = useRef(null);   // avoids stale closures in callbacks
  const modalCloseRef     = useRef(null);
  const customizeTriggerRef = useRef(null);
  const impressionTracked = useRef(false);

  // ── Config loader ─────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/cookie-consent/config`, {
          signal:  controller.signal,
          headers: { "Content-Type": "application/json" },
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setConfig(data);
          configRef.current = data;
        }
      } catch {
        // Graceful offline fallback — banner still works with defaults
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    };

    // Timeout after 4 s so a slow API never delays the banner indefinitely
    const timeout = setTimeout(() => {
      controller.abort();
      if (!cancelled) setConfigLoaded(true);
    }, 4000);

    load();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  // ── Banner visibility logic (runs after config is known) ─────────────────

  useEffect(() => {
    if (!configLoaded) return;

    const cfg = configRef.current;

    // Admin has disabled the popup entirely
    if (cfg?.isEnabled === false) return;

    const raw      = localStorage.getItem(STORAGE_KEY);
    const migrated = migrateStoredConsent(raw);

    // First-time visitor — no stored consent
    if (!migrated) {
      const delay = cfg?.initialDelay ?? 700;
      const timer = setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setBannerVisible(true), 50);
      }, delay);
      return () => clearTimeout(timer);
    }

    // Migrate legacy v1 string format
    if (raw === "all" || raw === "necessary") {
      persistConsent(migrated, cfg?.policyVersion || "1.0");
    }

    setPreferences((prev) => ({ ...prev, ...migrated }));

    // Check expiry
    const interval = cfg?.renewalInterval || "yearly";
    const maxAge   = RENEWAL_MS[interval] ?? RENEWAL_MS.yearly;
    const age      = migrated.timestamp ? Date.now() - migrated.timestamp : 0;
    if (maxAge !== Infinity && age > maxAge) {
      const delay = cfg?.initialDelay ?? 700;
      const t = setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setBannerVisible(true), 50);
      }, delay);
      return () => clearTimeout(t);
    }

    // Check policy version change
    const storedPV  = migrated.policyVersion;
    const currentPV = cfg?.policyVersion;
    if (currentPV && storedPV && storedPV !== currentPV) {
      const delay = cfg?.initialDelay ?? 700;
      const t = setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setBannerVisible(true), 50);
      }, delay);
      return () => clearTimeout(t);
    }
  }, [configLoaded]);

  // ── Global footer "Cookie Settings" event listener ────────────────────────

  useEffect(() => {
    const handle = () => {
      customizeTriggerRef.current = null;
      setModalOpen(true);
      setTimeout(() => setModalVisible(true), 20);
    };
    window.addEventListener("kaphyCookieSettingsOpen", handle);
    return () => window.removeEventListener("kaphyCookieSettingsOpen", handle);
  }, []);

  // ── Body scroll lock while modal is open ─────────────────────────────────

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
      const t = setTimeout(() => modalCloseRef.current?.focus(), 80);
      return () => {
        clearTimeout(t);
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
  }, [modalOpen]);

  // ── Event tracking ────────────────────────────────────────────────────────

  const trackEvent = useCallback((eventType, prefs) => {
    const payload = {
      eventType,
      sessionId:     getSessionId(),
      policyVersion: configRef.current?.policyVersion || "1.0",
      ...(prefs ? { preferences: prefs } : {}),
    };
    fetch(`${API_BASE}/api/cookie-consent/event`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
    }).catch(() => {});
  }, []);

  // Track one impression per session when banner first becomes visible
  useEffect(() => {
    if (bannerVisible && !impressionTracked.current) {
      impressionTracked.current = true;
      trackEvent("impression");
    }
  }, [bannerVisible, trackEvent]);

  // ── ESC key closes modal ──────────────────────────────────────────────────

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // ── Banner / modal dismiss helpers ────────────────────────────────────────

  const dismissBanner = useCallback(() => {
    setBannerVisible(false);
    setTimeout(() => setShowBanner(false), 400);
  }, []);

  const closeModal = useCallback((andDismiss = false) => {
    setModalVisible(false);
    setTimeout(() => {
      setModalOpen(false);
      if (andDismiss) {
        dismissBanner();
      } else {
        customizeTriggerRef.current?.focus();
      }
    }, 300);
  }, [dismissBanner]);

  // ── Consent actions ───────────────────────────────────────────────────────

  const currentPolicyVersion = () => configRef.current?.policyVersion || "1.0";

  const handleAcceptAll = useCallback(() => {
    const all = { essential: true, analytics: true, marketing: true, functional: true };
    setPreferences(all);
    persistConsent(all, currentPolicyVersion());
    trackEvent("accept_all", all);
    if (modalOpen) closeModal(true); else dismissBanner();
  }, [modalOpen, closeModal, dismissBanner, trackEvent]);

  const handleRejectNonEssential = useCallback(() => {
    const minimal = { ...DEFAULT_PREFS };
    setPreferences(minimal);
    persistConsent(minimal, currentPolicyVersion());
    trackEvent("reject_all", minimal);
    if (modalOpen) closeModal(true); else dismissBanner();
  }, [modalOpen, closeModal, dismissBanner, trackEvent]);

  const handleSavePreferences = useCallback(() => {
    persistConsent(preferences, currentPolicyVersion());
    trackEvent("save_preferences", preferences);
    setSaved(true);
    setTimeout(() => { setSaved(false); closeModal(true); }, 850);
  }, [preferences, closeModal, trackEvent]);

  const openModal = useCallback((triggerEl) => {
    customizeTriggerRef.current = triggerEl || null;
    trackEvent("customize_opened");
    setModalOpen(true);
    setTimeout(() => setModalVisible(true), 20);
  }, [trackEvent]);

  const togglePref = (id) => {
    if (id === "essential") return;
    setPreferences((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleExpand = (id) =>
    setExpandedCategory((prev) => (prev === id ? null : id));

  // ── Compute active categories (respects admin enable/disable) ─────────────

  const activeCategories = CATEGORIES_STATIC
    .filter((cat) => {
      if (cat.id === "essential") return true;
      if (!config?.categories?.[cat.id]) return true;
      return config.categories[cat.id].enabled !== false;
    })
    .map((cat) => {
      const cfgCat = config?.categories?.[cat.id];
      if (!cfgCat) return cat;
      return {
        ...cat,
        name:        cfgCat.name        || cat.name,
        description: cfgCat.description || cat.description,
        details:     cfgCat.details     || cat.details,
        examples:    cfgCat.examples?.length ? cfgCat.examples : cat.examples,
      };
    });

  // ── Render ────────────────────────────────────────────────────────────────

  // Nothing to render when both banner and modal are inactive
  if (!showBanner && !modalOpen) return null;

  // Text values — live config with inline fallbacks
  const txt = {
    heading:              config?.heading              || "Your Privacy Matters",
    description:          config?.description          || "We use cookies to secure your session, personalize your insurance experience, and improve our services.",
    acceptAllLabel:       config?.acceptAllLabel       || "Accept All",
    rejectLabel:          config?.rejectLabel          || "Reject Non-Essential",
    customizeLabel:       config?.customizeLabel       || "Customize",
    savePreferencesLabel: config?.savePreferencesLabel || "Save Preferences",
    modalTitle:           config?.modalTitle           || "Cookie Preferences",
    modalSubtitle:        config?.modalSubtitle        || "Manage how Kaphy Insurance Agency uses cookies",
  };

  return (
    <>
      {/* ── Banner ───────────────────────────────────────────────────────── */}
      {showBanner && (
        <div
          role="region"
          aria-label="Cookie consent"
          aria-live="polite"
          className={`kaphy-cookie-banner${bannerVisible ? " kaphy-cookie-banner--visible" : ""}`}
        >
          <div className="kaphy-cookie-banner__card">
            <div className="kaphy-cookie-banner__lead">
              <div className="kaphy-cookie-banner__icon-wrap" aria-hidden="true">
                <ShieldIcon />
              </div>
              <div className="kaphy-cookie-banner__copy">
                <p className="kaphy-cookie-banner__title">{txt.heading}</p>
                <p className="kaphy-cookie-banner__desc">
                  {txt.description}{" "}
                  <a href="/privacy" className="kaphy-cookie-link">Privacy Policy</a>
                  <span className="kaphy-cookie-separator" aria-hidden="true">·</span>
                  <a href="/terms" className="kaphy-cookie-link">Terms of Service</a>
                </p>
              </div>
            </div>

            <div className="kaphy-cookie-banner__actions">
              <button
                type="button"
                className="kaphy-cookie-btn kaphy-cookie-btn--ghost"
                onClick={handleRejectNonEssential}
                aria-label="Reject non-essential cookies"
              >
                {txt.rejectLabel}
              </button>
              <button
                type="button"
                className="kaphy-cookie-btn kaphy-cookie-btn--outline"
                onClick={(e) => openModal(e.currentTarget)}
                aria-haspopup="dialog"
                aria-label="Customize cookie preferences"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2.2" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" />
                </svg>
                {txt.customizeLabel}
              </button>
              <button
                type="button"
                className="kaphy-cookie-btn kaphy-cookie-btn--primary"
                onClick={handleAcceptAll}
                aria-label="Accept all cookies"
              >
                {txt.acceptAllLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preferences Modal ────────────────────────────────────────────── */}
      {modalOpen && (
        <div
          className={`kaphy-cookie-overlay${modalVisible ? " kaphy-cookie-overlay--visible" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kcc-modal-title"
          aria-describedby="kcc-modal-desc"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className={`kaphy-cookie-modal${modalVisible ? " kaphy-cookie-modal--visible" : ""}`}>

            {/* Modal Header */}
            <div className="kaphy-cookie-modal__header">
              <div className="kaphy-cookie-modal__header-lead">
                <div className="kaphy-cookie-modal__header-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" fill="url(#mod-shield)" />
                    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    <defs>
                      <linearGradient id="mod-shield" x1="3" y1="2" x2="21" y2="23" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#3b82f6" /><stop offset="1" stopColor="#1d4ed8" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div>
                  <h2 id="kcc-modal-title" className="kaphy-cookie-modal__title">{txt.modalTitle}</h2>
                  <p id="kcc-modal-desc" className="kaphy-cookie-modal__subtitle">{txt.modalSubtitle}</p>
                </div>
              </div>
              <button
                ref={modalCloseRef}
                type="button"
                className="kaphy-cookie-modal__close"
                onClick={() => closeModal()}
                aria-label="Close cookie preferences"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="kaphy-cookie-modal__body">
              <p className="kaphy-cookie-modal__intro">
                We use different types of cookies to enhance your experience. Toggle the
                categories below to control what data you share. Essential cookies cannot
                be disabled — they keep the site secure and functional.
              </p>

              <div className="kaphy-cookie-categories" role="list">
                {activeCategories.map((cat, i) => {
                  const isExpanded = expandedCategory === cat.id;
                  return (
                    <div
                      key={cat.id}
                      role="listitem"
                      className={`kaphy-cookie-category${isExpanded ? " kaphy-cookie-category--expanded" : ""}`}
                      style={{ "--delay": `${i * 55}ms` }}
                    >
                      <div className="kaphy-cookie-category__row">
                        <button
                          type="button"
                          className="kaphy-cookie-category__expand-btn"
                          onClick={() => toggleExpand(cat.id)}
                          aria-expanded={isExpanded}
                          aria-controls={`kcc-cat-${cat.id}`}
                        >
                          <span className="kaphy-cookie-category__cat-icon">{cat.icon}</span>
                          <span className="kaphy-cookie-category__info">
                            <span className="kaphy-cookie-category__name">{cat.name}</span>
                            <span className="kaphy-cookie-category__desc">{cat.description}</span>
                          </span>
                          <svg className="kaphy-cookie-category__chevron" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>

                        <div className="kaphy-cookie-category__control">
                          {cat.alwaysOn ? (
                            <span className="kaphy-cookie-always-on" aria-label="Always enabled">Always on</span>
                          ) : (
                            <label
                              className="kaphy-cookie-toggle"
                              aria-label={`${preferences[cat.id] ? "Disable" : "Enable"} ${cat.name} cookies`}
                            >
                              <input
                                type="checkbox"
                                className="kaphy-cookie-toggle__input"
                                checked={!!preferences[cat.id]}
                                onChange={() => togglePref(cat.id)}
                              />
                              <span className="kaphy-cookie-toggle__track" aria-hidden="true">
                                <span className="kaphy-cookie-toggle__thumb" />
                              </span>
                            </label>
                          )}
                        </div>
                      </div>

                      <div
                        id={`kcc-cat-${cat.id}`}
                        className="kaphy-cookie-category__details"
                        aria-hidden={!isExpanded}
                      >
                        <p className="kaphy-cookie-category__details-body">{cat.details}</p>
                        <ul className="kaphy-cookie-category__chips" aria-label={`Examples for ${cat.name}`}>
                          {cat.examples.map((ex) => (
                            <li key={ex} className="kaphy-cookie-category__chip">{ex}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="kaphy-cookie-modal__footer">
              <div className="kaphy-cookie-modal__footer-links">
                <a href="/privacy" className="kaphy-cookie-link kaphy-cookie-link--sm">Privacy Policy</a>
                <span className="kaphy-cookie-separator" aria-hidden="true">·</span>
                <a href="/terms" className="kaphy-cookie-link kaphy-cookie-link--sm">Terms of Service</a>
              </div>
              <div className="kaphy-cookie-modal__footer-actions">
                <button
                  type="button"
                  className="kaphy-cookie-btn kaphy-cookie-btn--ghost kaphy-cookie-btn--sm"
                  onClick={handleRejectNonEssential}
                >
                  Reject All
                </button>
                <button
                  type="button"
                  className="kaphy-cookie-btn kaphy-cookie-btn--outline kaphy-cookie-btn--sm"
                  onClick={handleAcceptAll}
                >
                  {txt.acceptAllLabel}
                </button>
                <button
                  type="button"
                  className={`kaphy-cookie-btn kaphy-cookie-btn--primary kaphy-cookie-btn--sm${saved ? " kaphy-cookie-btn--saved" : ""}`}
                  onClick={handleSavePreferences}
                  aria-label="Save your cookie preferences"
                >
                  {saved ? (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Saved
                    </>
                  ) : (
                    txt.savePreferencesLabel
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
